import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useWebSocket } from "@/react/hooks/useWebSocket";
import type {
  ChatRoomMessageSearchResult,
  ChatThreadMessage,
} from "@/react/types/Chat/ChatRoomMessage";
import { markChatThreadsAsRead } from "@circle-react/api";
import { CHAT_THREADS_BANNER_LOCAL_STORAGE_KEY } from "@circle-react/components/constants";
import { usePunditUserContext, useSpacesContext } from "@circle-react/contexts";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import {
  useChatThreadsItem,
  useChatThreadsList,
  useUnreadChatThreads,
} from "@circle-react/hooks/chatsV2";
import { isElementInViewPort } from "@circle-react/hooks/chatsV2/helper";
import { useLocalStorageState } from "@circle-react/hooks/utils/useLocalStorageState";
import { Loader } from "@circle-react-uikit/Loader";
import { SkeletonLoader } from "@circle-react-uikit/SkeletonLoader";
import { ChatThreadsBanner } from "./ChatThreadsBanner";
import { EmptyChatThreads } from "./EmptyChatThreads";
import { ThreadItem } from "./ThreadItem";
import { UnreadThreadIndicatorButton } from "./UnreadThreadIndicatorButton";
import { getSeparateSortedUnreadThreadIds } from "./helpers";

interface ChatThreadsListProps {
  isRenderedOnHeader?: boolean;
}

interface SelectedReply {
  parentMessage: ChatRoomMessageSearchResult | null;
  threadMessageId: string | null;
}

interface Thread {
  chat_room: {
    embedded_space_id: number;
    kind: string;
    lesson: any;
    name: string;
  };
  current_participant: any;
  parent_message: {
    chat_room_uuid: string;
    chat_thread_id: number;
    embedded: boolean;
    id: number;
    replies_count: number;
    thread_participants_preview: any[];
    total_thread_participants_count: number;
  };
  replies: any[];
}

interface WebsocketReactionData {
  event: "createReaction" | "destroyReaction";
  json_message: {
    community_member_id: number;
    emoji: string;
    reactionable_id: number;
    reactionable_type: string;
  };
}

export const ChatThreadsList = ({
  isRenderedOnHeader = false,
}: ChatThreadsListProps) => {
  const { isViewOnlyMasquerading, currentCommunityMember } =
    usePunditUserContext();
  const [shouldShowChatThreadsBanner, setShouldShowChatThreadsBanner] =
    useLocalStorageState(CHAT_THREADS_BANNER_LOCAL_STORAGE_KEY, true);
  const [selectedReply, setSelectedReply] = useState<SelectedReply>({
    parentMessage: null,
    threadMessageId: null,
  });
  const { unreadChatThreadIds, newBadgeIds, resetNewBadgeIds } =
    useUnreadChatThreads();
  const {
    chatThreads = [],
    isLoading,
    hasNextPage,
    fetchNextPage,
    isChatThreadsAvailable,
    onEventReceive,
    updateChatThreads,
    onReactionEventReceive,
  }: any = useChatThreadsList();

  const { threadMessages = [] } = useChatThreadsItem({
    parentMessage: selectedReply.parentMessage as ChatRoomMessageSearchResult,
    threadMessageId: selectedReply.threadMessageId as string,
    successCallback: () => {
      setSelectedReply({
        parentMessage: null,
        threadMessageId: null,
      });
    },
  });

  const { records: spaces, isLoading: isSpacesContextLoading } =
    useSpacesContext();

  useEffect(() => {
    threadMessages.length > 0 && updateChatThreads(threadMessages);
  }, [threadMessages]);

  useWebSocket(
    {
      channel: "Chats::CommunityMemberThreadsChannel",
      onMessageReceive: (data: any) => {
        if (data.event === "newMessage") {
          markThreadAsReadIfInView(data.json_message);
        }
        onEventReceive(data);
      },
      community_member_id: currentCommunityMember?.id,
      canCreateConnection: Boolean(currentCommunityMember?.id),
    },
    [],
  );

  useWebSocket(
    {
      channel: "ChatRoomChannel",
      onMessageReceive: (data: WebsocketReactionData) => {
        if (
          data.event === "createReaction" ||
          data.event === "destroyReaction"
        ) {
          onReactionEventReceive(data);
        }
      },
      community_member_id: currentCommunityMember?.id,
      canCreateConnection: Boolean(currentCommunityMember?.id),
    },
    [currentCommunityMember?.id],
  );

  const markThreadAsReadIfInView = (message: ChatThreadMessage) => {
    const threadId = message.chat_thread_id;
    if (!threadId) return;
    const threadElement = document.getElementById(String(threadId));
    if (
      threadElement &&
      isElementInViewPort(threadElement) &&
      !message.unread_flow
    ) {
      void markChatThreadsAsRead(threadId);
    }
  };

  const handleWhenThreadInViewPort = (
    inView: boolean,
    thread: ChatThreadMessage,
  ) => {
    if (!inView) return;
    const { parent_message: parentMessage } = thread;
    if (unreadChatThreadIds.includes(parentMessage.chat_thread_id)) {
      void markChatThreadsAsRead(parentMessage.chat_thread_id);
    }
  };

  useEffect(
    () => () => {
      resetNewBadgeIds();
    },
    [],
  );

  if ((isLoading || isSpacesContextLoading) && !isChatThreadsAvailable) {
    return (
      <SkeletonLoader
        variant={
          isRenderedOnHeader
            ? "chat-threads-list-header"
            : "chat-threads-list-page"
        }
      />
    );
  }

  if (!isChatThreadsAvailable) {
    return <EmptyChatThreads isRenderedOnHeader={isRenderedOnHeader} />;
  }

  const { unreadThreadIdsAtTop, unreadThreadIdsAtBottom } =
    getSeparateSortedUnreadThreadIds(unreadChatThreadIds, chatThreads);

  return (
    <div className="w-full" id="threads-scroll-view">
      <UnreadThreadIndicatorButton
        unreadChatThreadIds={unreadThreadIdsAtTop}
        isTopButton
      />
      <ChatThreadsBanner
        shouldShowChatThreadsBanner={
          Boolean(shouldShowChatThreadsBanner) && !isRenderedOnHeader
        }
        hideBanner={() => setShouldShowChatThreadsBanner(false)}
      />
      <InfiniteScroll
        style={{
          overflow: "auto",
        }}
        scrollThreshold={0.8}
        next={fetchNextPage}
        hasMore={Boolean(hasNextPage)}
        dataLength={chatThreads.length}
        className={classNames({
          "mb-14 px-3 py-6 md:px-6 lg:mb-0": !isRenderedOnHeader,
          "mb-4 flex flex-col gap-y-2": isRenderedOnHeader,
        })}
        scrollableTarget={isRenderedOnHeader ? "chat-threads-header" : "panel"}
        loader={<Loader center />}
      >
        {chatThreads.map((thread: Thread) => {
          const {
            parent_message: parentMessage,
            current_participant,
            chat_room,
          } = thread;

          const {
            embedded_space_id: embeddedSpaceId,
            name: chatRoomName,
            kind: chatRoomKind,
            lesson,
          } = chat_room;

          const { chat_room_uuid: chatRoomUuid, id: parentMessageId } =
            parentMessage;

          const space = spaces.find(item => item.id === embeddedSpaceId);
          const roomName = space ? space.name : chatRoomName;

          const chatRoomData = {
            uuid: chatRoomUuid,
            current_participant,
            chat_room_kind: chatRoomKind,
            embedded_space: space,
            roomName,
            lesson,
          };

          return (
            <ThreadItem
              key={parentMessageId}
              chatRoomData={chatRoomData}
              newBadgeIds={newBadgeIds}
              handleWhenThreadInViewPort={handleWhenThreadInViewPort}
              thread={thread}
              space={space}
              setSelectedReply={setSelectedReply}
              isRenderedOnHeader={isRenderedOnHeader}
              isViewOnlyMasquerading={isViewOnlyMasquerading}
            />
          );
        })}
      </InfiniteScroll>
      <UnreadThreadIndicatorButton
        unreadChatThreadIds={unreadThreadIdsAtBottom}
      />
    </div>
  );
};
